import { ReactNode } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { buttonVariants } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import {
  MenubarCheckboxItem,
  MenubarContent,
  MenubarItem,
  MenubarLabel,
  MenubarMenu,
  MenubarSeparator,
  MenubarShortcut,
  MenubarTrigger,
} from "@/components/ui/menubar";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

interface ResponsiveMenuProps {
  label: string;
  icon: ReactNode;
  disabled?: boolean;
  contentClassName?: string;
  onClose?: () => void;
  children: ReactNode;
}

export function ResponsiveMenu({
  label,
  icon,
  disabled,
  contentClassName,
  onClose,
  children,
}: ResponsiveMenuProps) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Sheet onOpenChange={(open) => !open && onClose?.()}>
        <SheetTrigger
          className={cn(
            buttonVariants({ variant: "ghost" }),
            "h-8 w-8 p-0 rounded-sm",
          )}
          disabled={disabled}
          aria-label={label}
        >
          {icon}
        </SheetTrigger>
        <SheetContent
          side="bottom"
          className={cn(
            "rounded-t-xl p-0 pb-[env(safe-area-inset-bottom)]",
            contentClassName,
          )}
        >
          <SheetTitle className="px-4 pt-4 pb-2 text-base">{label}</SheetTitle>
          <div className="flex max-h-[70dvh] flex-col gap-1 overflow-y-auto px-2 pb-4">
            {children}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <MenubarMenu>
      <MenubarTrigger disabled={disabled}>{label}</MenubarTrigger>
      <MenubarContent className={contentClassName} onCloseAutoFocus={onClose}>
        {children}
      </MenubarContent>
    </MenubarMenu>
  );
}

interface ResponsiveMenuItemProps {
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  children: ReactNode;
}

export function ResponsiveMenuItem({
  onClick,
  disabled,
  className,
  children,
}: ResponsiveMenuItemProps) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <SheetClose asChild>
        <button
          type="button"
          disabled={disabled}
          onClick={onClick}
          className={cn(
            "flex w-full items-center gap-2 rounded-md px-3 py-3 text-left text-sm hover:bg-accent disabled:pointer-events-none disabled:opacity-50",
            className,
          )}
        >
          {children}
        </button>
      </SheetClose>
    );
  }

  return (
    <MenubarItem onClick={onClick} disabled={disabled} className={className}>
      {children}
    </MenubarItem>
  );
}

interface ResponsiveMenuCheckboxItemProps {
  checked: boolean;
  onCheckedChange: (value: boolean) => void;
  onSelect?: (event: Event) => void;
  className?: string;
  children: ReactNode;
}

export function ResponsiveMenuCheckboxItem({
  checked,
  onCheckedChange,
  onSelect,
  className,
  children,
}: ResponsiveMenuCheckboxItemProps) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <label
        className={cn(
          "flex w-full cursor-pointer items-center gap-3 rounded-md px-3 py-3 text-sm hover:bg-accent",
          className,
        )}
      >
        <Checkbox checked={checked} onCheckedChange={onCheckedChange} />
        <span className="grow">{children}</span>
      </label>
    );
  }

  return (
    <MenubarCheckboxItem
      checked={checked}
      onCheckedChange={onCheckedChange}
      onSelect={onSelect}
      className={className}
    >
      {children}
    </MenubarCheckboxItem>
  );
}

export function ResponsiveMenuLabel({ children }: { children: ReactNode }) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <div className="px-3 pt-3 pb-1 text-lg font-semibold">{children}</div>
    );
  }

  return <MenubarLabel>{children}</MenubarLabel>;
}

export function ResponsiveMenuSeparator() {
  const isMobile = useIsMobile();

  if (isMobile) {
    return <Separator className="my-1" />;
  }

  return <MenubarSeparator />;
}

export function ResponsiveMenuShortcut({ children }: { children: ReactNode }) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return null;
  }

  return <MenubarShortcut>{children}</MenubarShortcut>;
}
